import useSessionStore from '@/stores/useSessionStore'
import clsx from 'clsx'
import { useModal } from 'lib/components/Modal/Modal'
import { SubBrandName } from 'lib/settings/brands/index'
import { subBrands } from 'lib/settings/brands/sub-brands'
import { useEffect, useState } from 'react'
import OtpLogin, {
  OtpLoginSuccessDetails,
} from '../AccountPrompt/OtpLogin/OtpLogin'
import PasswordResetWithCode, {
  PasswordResetSuccessDetails,
} from '../AccountPrompt/PasswordResetWithCode/PasswordResetWithCode'
import Logo from '../Logo/Logo'
import Modal from '../Modal/Modal'
import Notification from '../Notification/Notification'
import LoginModalIndex from './screens'

import MobileVerify from '../MobileVerification/MobileVerify'
import { useGetUserApi } from '../MobileVerification/hooks/useGetUserApi'

export const LOGIN_MODAL_ID = 'login-modal'

export interface LoginModalProps {
  /**
   * Brand identifier
   */
  brand?: SubBrandName
  /**
   * Optional callback function when the modal is displayed.
   */
  onDisplay?: () => void
  /**
   * Optional callback function when the form is submitted.
   */
  onSubmit?: (data: Record<string, any>) => void
  /**
   * Callback after the user resets their password via verification code.
   */
  onPasswordResetComplete?: (details: PasswordResetSuccessDetails) => void
  /**
   * Callback after the user logs in via OTP.
   */
  onOtpLoginComplete?: (details: OtpLoginSuccessDetails) => void
  /**
   * Cart token for associating guest cart on OTP login.
   */
  cartToken?: string
  /**
   * Callback function on login.
   */
  login: () => void
  /**
   * Callback function for Google login.
   */
  googleLogin: () => void
  /**
   * Callback function for Facebook login.
   */
  facebookLogin: () => void
  /**
   * Callback function for Apple login.
   */
  appleLogin: () => void
  /**
   * Error message to display on modal.
   */
  errorMessage?: string
  /**
   * indicate whether authentication was successful
   */
  authSuccessful?: boolean
}

export default function LoginModal({
  brand = 'shipplay',
  onDisplay,
  onSubmit,
  onPasswordResetComplete,
  onOtpLoginComplete,
  cartToken,
  login,
  googleLogin,
  facebookLogin,
  appleLogin,
  errorMessage,
  authSuccessful = false,
}: LoginModalProps) {
  const [hideDialog] = useModal((state) => [state.hideDialog])
  const [currentScreen, setCurrentScreen] = useState('index')
  const [userEmail, setUserEmail] = useState<string | undefined>(undefined)
  const sessionEmail = useSessionStore((store) => store.userEmail)

  const getDialogArgs = useModal((state) => state.getDialogArgs)
  const dialogArgs = getDialogArgs(LOGIN_MODAL_ID)

  const { data: currentUser } = useGetUserApi(userEmail || '')

  useEffect(() => {
    if (dialogArgs) {
      const { email } = dialogArgs
      if (email && email.indexOf('@') > 0) {
        setUserEmail(email)
      }
    }
  }, [dialogArgs])

  useEffect(() => {
    if (authSuccessful && sessionEmail) {
      setUserEmail(sessionEmail)
    }
  }, [authSuccessful, sessionEmail])

  const subBrand = subBrands[brand]
  const handleForgotLinkClick:
    | React.MouseEventHandler<HTMLAnchorElement>
    | undefined = (e) => {
    e.preventDefault()
    setCurrentScreen('forgot')
  }

  const handleOtpLinkClick = (): void => {
    setCurrentScreen('otp')
  }

  useEffect(() => {
    if (authSuccessful && currentUser) {
      // If the user is authenticated and is mobile verified hide the dialog
      // Otherwise, show the mobile verification screen
      if (
        currentUser.mobileVerificationEligible &&
        !currentUser.mobileVerified
      ) {
        setCurrentScreen('mobile-verify')
      } else {
        hideDialog(LOGIN_MODAL_ID)
      }
    }
  }, [currentUser, authSuccessful, hideDialog])

  return (
    <Modal
      id={LOGIN_MODAL_ID}
      beforeShow={onDisplay}
      className="!p-12"
      dialogPanelClass="!max-w-[343px] md:!max-w-[424px] w-full"
      content={
        <div>
          <Logo
            brand={subBrand.logo.textOnlyDark}
            width="160"
            height="32"
            className="mx-auto mb-6"
          />
          <div className={clsx(currentScreen === 'index' ? 'block' : 'hidden')}>
            {errorMessage && (
              <Notification
                size="small"
                icon={{ icon: 'error' }}
                type="urgent"
                title={errorMessage}
                className="mb-4 !text-left"
              />
            )}
            <LoginModalIndex
              onSubmit={onSubmit}
              login={login}
              googleLogin={googleLogin}
              facebookLogin={facebookLogin}
              appleLogin={appleLogin}
              handleForgotLinkClick={handleForgotLinkClick}
              handleOtpLinkClick={handleOtpLinkClick}
              defaultEmail={currentUser?.email}
              brand={brand}
            />
          </div>
          <div
            className={clsx(currentScreen === 'forgot' ? 'block' : 'hidden')}
          >
            <PasswordResetWithCode
              onBackToLogin={() => setCurrentScreen('index')}
              onResetComplete={(details) => {
                setCurrentScreen('index')
                onPasswordResetComplete?.(details)
              }}
            />
          </div>

          {currentScreen === 'otp' && (
            <OtpLogin
              onBackToLogin={() => setCurrentScreen('index')}
              onOtpLoginComplete={(details) => {
                onOtpLoginComplete?.(details)
                setUserEmail(details.email)
              }}
              cartToken={cartToken}
            />
          )}

          {currentScreen == 'mobile-verify' && currentUser && (
            <MobileVerify
              phoneNumber={currentUser.phoneNumber}
              showPhoneNumber={true}
              onVerificationSuccess={() => hideDialog(LOGIN_MODAL_ID)}
              onSkipVerification={() => hideDialog(LOGIN_MODAL_ID)}
              brand={brand}
            />
          )}
        </div>
      }
      afterHide={() => setCurrentScreen('index')}
    />
  )
}
